import { AuthenticationService } from '@servicios/authentication.service';
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LoginDialogComponent } from './auth/loginDialog/loginDialog.component';
import { Subscription } from 'rxjs';
import { NotificacionesService } from '@servicios/notificaciones.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterEvent } from '@angular/router';
import { ConfigService } from '@servicios/config.service';
import { BreakpointService } from '@servicios/breakpoint.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

  sesionExpirada$ = new Subscription();
  closeDialogs$ = new Subscription();
  routerEventsSubs = new Subscription();
  isLoading = false;
  private loginDialogRef: DynamicDialogRef | null = null;

  @HostListener('window: beforeunload') async ngOnDestroy() {
    this.sesionExpirada$.unsubscribe();
    this.routerEventsSubs.unsubscribe();
    this.closeDialogs$.unsubscribe();
    this.dialog.closeAll();
  }

  @HostListener('document:visibilitychange')
  @HostListener('window:focus')
  checkIdleOnVisibilityChange() {
    if (this.AuthenticationService.authorized.value.userAuth) {
      this.AuthenticationService.checkIdleTimeout();
    }
  }

  @HostListener('window: click')
  @HostListener('window: keyup')
  @HostListener('window: scroll')
  @HostListener('window: pointerdown') resetTimerByKey() {
    if (this.AuthenticationService.authorized.value.userAuth) {
      this.AuthenticationService.resetSessionTimer();
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleGlobalShortcuts(event: KeyboardEvent) {
    const isAltL = event.altKey && !event.ctrlKey && !event.metaKey && (event.key === 'l' || event.key === 'L');
    const isCtrlShiftL = (event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'l' || event.key === 'L');

    if (isAltL || isCtrlShiftL) {
      if (this.AuthenticationService.authorized.value.userAuth && !this.AuthenticationService.sesionExpirada) {
        event.preventDefault();
        event.stopPropagation();
        this.AuthenticationService.lockSession();
      }
    }
  }

  constructor(
    private AuthenticationService: AuthenticationService,
    private dialog: MatDialog,
    private ns: NotificacionesService,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialogService: DialogService,
    public configService: ConfigService,
    private bs: BreakpointService,
    private messageService: MessageService
  ) { }

  reloadPage() {
    window.location.reload();
  }

  ngOnInit() {
    this.configService.isOnline$()

    // Check if the user is already authenticated when loading.
    this.AuthenticationService.autoAuth();
    this.bs.initBreakpointObserver();

    this.closeDialogs$ = this.AuthenticationService.closeDialogs$.subscribe(value => {
      if (value) {
        this.dialogService.dialogComponentRefMap.forEach((dialog, ref) => {
          if (this.AuthenticationService.sesionExpirada && ref === this.loginDialogRef) {
            return;
          }
          ref.close();
        });
        if (!this.AuthenticationService.sesionExpirada && this.loginDialogRef) {
          this.loginDialogRef.close();
          this.loginDialogRef = null;
          document.body.classList.remove('session-expired');
        }
      }
    })

    // Subcribe to observable to know if the session has expired.
    this.sesionExpirada$ = this.AuthenticationService.sesionExpiradaSubject$
      .subscribe(value => {
        if (value.value) {
          const currentUrl = this.router.url || window.location.pathname;
          if (currentUrl.includes('/login') || currentUrl.includes('/loginClient') || currentUrl.includes('/loadingPage')) {
            return;
          }

          if (this.loginDialogRef) {
            return;
          }

          this.dialogService.dialogComponentRefMap.forEach((dialog, ref) => {
            if (ref !== this.loginDialogRef) ref.close();
          });

          document.body.classList.add('session-expired');
          this.openDialogLogin(value.user);
        }
      })

    // Subscribe to notification service to emit messages in every app section.
    this.ns.notificationSubject$.subscribe({
      next: data => {
        if (!data.emit && data.message != "Registros devueltos" && data.message && typeof (data.message) == 'string') {
          this.snackBar.open(data.message, null, { duration: 2000 })
        }
      },
      error: err => this.snackBar.open('Ocurrio un error en el servicio de notificaciones', null, { duration: 2000 }),
    }
    )

    this.routerEventsSubs = this.router.events
      .subscribe(
        (e) => this.navigationInterceptor(e as RouterEvent)
      )
  }

  // Shows and hides the loading spinner during RouterEvent changes
  navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      if (this.AuthenticationService.sesionExpirada) {
        const isLogin = event.url.includes('/login') || event.url.includes('/loginClient');
        if (!isLogin) {
          if (this.loginDialogRef) {
            this.loginDialogRef.close();
            this.loginDialogRef = null;
          }
          document.body.classList.remove('session-expired');
          this.AuthenticationService.sesionExpirada = false;

          const isClient = event.url.startsWith('/clients') || this.router.url.startsWith('/clients');
          const targetLogin = isClient ? '/loginClient' : '/login';
          this.AuthenticationService.logout(undefined, targetLogin);
          return;
        }
        return;
      }

      this.isLoading = true

      // Close all PrimeNG DynamicDialogs on navigation start (except login)
      this.dialogService.dialogComponentRefMap.forEach((dialog, ref) => {
        if (ref !== this.loginDialogRef) ref.close();
      });

      // Close all Material dialogs on navigation start
      this.dialog.closeAll();
    }

    if (event instanceof NavigationEnd) {
      let url = event.url;
      if (url == '/loadingPage') url = '/'
      if (url != '/backup' && url !== '/' && !url.includes('/login')) {
        if(sessionStorage.getItem('currentURL') !== url) sessionStorage.setItem('previousURL', sessionStorage.getItem('currentURL') || '')
        sessionStorage.setItem('currentURL', url)
      }
      if (url.includes('/login') || url.includes('/loginClient')) {
        this.dialog.closeAll();
        if (this.loginDialogRef) {
          this.loginDialogRef.close();
          this.loginDialogRef = null;
        }
        document.body.classList.remove('session-expired');
      }
      this.isLoading = false
      this.cleanOrphanMasks();
    }

    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel || event instanceof NavigationError) {
      this.isLoading = false
      this.cleanOrphanMasks();
    }
  }

  /**
   * Cleans up orphaned modal/drawer masks left in the DOM by PrimeNG or Angular CDK
   * when components are destroyed before or during leave animations.
   */
  private cleanOrphanMasks(): void {
    setTimeout(() => {
      // Do not clean masks if session expiration re-login modal is active
      if (this.AuthenticationService.sesionExpirada || this.loginDialogRef) {
        return;
      }

      // 1. Clean orphaned PrimeNG drawer masks (.p-drawer-mask)
      const drawerMasks = document.querySelectorAll<HTMLElement>('.p-drawer-mask');
      const hasActiveModalDrawer = !!document.querySelector('.p-drawer-active, .p-drawer.p-drawer-visible');
      drawerMasks.forEach(mask => {
        if (!hasActiveModalDrawer || mask.classList.contains('p-overlay-mask-leave')) {
          mask.remove();
        }
      });

      // 2. Clean orphaned PrimeNG dialog masks (.p-dialog-mask)
      const dialogMasks = document.querySelectorAll<HTMLElement>('.p-dialog-mask');
      dialogMasks.forEach(mask => {
        const hasVisibleDialog = !!mask.querySelector('.p-dialog:not(.p-dialog-leave)');
        if (!hasVisibleDialog || mask.classList.contains('p-overlay-mask-leave')) {
          mask.remove();
        }
      });

      // 3. Clean generic PrimeNG overlay masks (.p-overlay-mask)
      const genericMasks = document.querySelectorAll<HTMLElement>('.p-overlay-mask:not(.p-drawer-mask):not(.p-dialog-mask)');
      genericMasks.forEach(mask => {
        if (mask.classList.contains('p-overlay-mask-leave') || !mask.children.length) {
          mask.remove();
        }
      });

      // 4. Clean orphaned CDK overlay backdrops
      const cdkBackdrops = document.querySelectorAll<HTMLElement>('.cdk-overlay-backdrop');
      cdkBackdrops.forEach(backdrop => {
        const overlayPane = backdrop.parentElement?.querySelector('.cdk-overlay-pane');
        if (!overlayPane || !overlayPane.children.length) {
          backdrop.remove();
        }
      });

      // 5. Restore body scrolling if no modals or dialogs remain
      const anyActiveModal = document.querySelector('.p-drawer-active, .p-dialog:not(.p-dialog-leave), .cdk-overlay-pane:not(:empty)');
      if (!anyActiveModal) {
        document.body.classList.remove('p-overflow-hidden');
      }
    }, 50);
  }

  openDialogLogin(data: { user: string, _id: string, client?: boolean }) {
    const currentUrl = this.router.url || window.location.pathname;
    if (currentUrl.includes('/login') || currentUrl.includes('/loginClient') || currentUrl.includes('/loadingPage')) {
      return;
    }

    if (this.loginDialogRef) {
      console.log('🚫 Intento de abrir duplicado del diálogo de login bloqueado.');
      return;
    }

    this.loginDialogRef = this.dialogService.open(LoginDialogComponent, {
      width: '500px',
      modal: true,
      closable: false,
      showHeader: false, // Custom artistic header inside component
      maskStyleClass: 'login-dialog-mask',
      closeOnEscape: false,
      contentStyle: { padding: '0', overflow: 'hidden', 'border-radius': '32px' },
      data: data
    });

    if (this.loginDialogRef) {
      this.loginDialogRef.onClose.subscribe(
        () => {
          // Solo limpieza visual. El logout/navigate ya fue manejado
          // desde el click del botón en loginDialog (dentro de Angular zone).
          this.loginDialogRef = null;
          document.body.classList.remove('session-expired');
        });
    }
  }

  visible: boolean = false;
    onConfirm() {
        this.messageService.clear('confirm');
        this.visible = false;
    }

    onReject() {
        this.messageService.clear('confirm');
        this.visible = false;
    }

    getIconClass(severity: string): string {
        const iconMap: { [key: string]: string } = {
          success: 'pi pi-check-circle',
          error: 'pi pi-times-circle',
          warn: 'pi pi-exclamation-triangle',
          info: 'pi pi-info-circle',
          custom: 'pi pi-star',
          default: 'pi pi-bell'
        };

      return iconMap[severity] || iconMap['default'];
    }
}
